import { formatINR } from '../../../shared/utils/formatters';
import React, { useEffect, useState } from 'react'
import { Table, Tag, Button, Space, message, Typography } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import { apiClient as api } from '../../../shared/api/axios-instance'

interface MerchantInvoiceListProps {
    merchantId: string;
}

interface Invoice {
    id: number;
    invoice_number: string;
    period_start: string;
    period_end: string;
    active_users_count: number;
    base_amount: number;
    gst_amount: number;
    total_amount: number;
    prorata_credit_amount: number;
    status: string;
    merchant_gstin: string | null;
    created_at: string;
}

export const MerchantInvoiceList: React.FC<MerchantInvoiceListProps> = ({ merchantId }) => {
    const [invoices, setInvoices] = useState<Invoice[]>([])
    const [loading, setLoading] = useState(false)

    const fetchInvoices = async () => {
        try {
            setLoading(true)
            const response = await api.get(`/admin/merchants/${merchantId}/invoices`)
            setInvoices(response.data.data || [])
        } catch (error: any) {
            message.error('Failed to load invoices.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchInvoices()
    }, [merchantId])

    const columns = [
        {
            title: 'Invoice Number',
            dataIndex: 'invoice_number',
            key: 'invoice_number',
            render: (text: string) => <Typography.Text strong>{text}</Typography.Text>
        },
        {
            title: 'Billing Period',
            key: 'period',
            render: (_: any, record: Invoice) => (
                <span>
                    {new Date(record.period_start).toLocaleDateString()} - {new Date(record.period_end).toLocaleDateString()}
                </span>
            )
        },
        {
            title: 'Users',
            dataIndex: 'active_users_count',
            key: 'users',
        },
        {
            title: 'Base + GST',
            key: 'amounts',
            render: (_: any, record: Invoice) => (
                <Space direction="vertical" size="small">
                    <Typography.Text>Base: {formatINR(Number(record.base_amount).toFixed(2))}</Typography.Text>
                    <Typography.Text type="secondary">GST: {formatINR(Number(record.gst_amount).toFixed(2))}</Typography.Text>
                    <Typography.Text strong>Total: {formatINR(Number(record.total_amount).toFixed(2))}</Typography.Text>
                </Space>
            )
        },
        {
            title: 'Prorata / Credit',
            dataIndex: 'prorata_credit_amount',
            key: 'prorata_credit_amount',
            render: (val: number) => val > 0 ? (
                <Tag color="green">Cr: {formatINR(Number(val).toFixed(2))}</Tag>
            ) : '-'
        },
        {
            title: 'GSTIN',
            dataIndex: 'merchant_gstin',
            key: 'merchant_gstin',
            render: (val: string | null) => val || 'Not Registered'
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                const color = status === 'paid' ? 'green' : status === 'pending' ? 'orange' : 'red';
                return <Tag color={color}>{status.toUpperCase()}</Tag>
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: Invoice) => (
                <Button type="link" icon={<EyeOutlined />}>View Details</Button>
            )
        }
    ]

    return (
        <Table
            columns={columns}
            dataSource={invoices}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
        />
    )
}
